import { Controller, type UseFormReturn } from "react-hook-form";
import { Plus } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { getErrors } from "../../../../utils";
import type { DemandFormValues } from "../../schema";
import {
  CommodityLineRow,
  type CommodityOption,
  type QualityOption,
  type UnitOption,
} from "../CommodityLineRow";
import { MatchingDepositsPanel, type BrowseMarketParams } from "../MatchingDepositsPanel";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export type { CommodityOption, QualityOption, UnitOption };

export interface DepositLine {
  key: string;
  commodityId: number;
  quality: string;
  unitOfMeasureId?: number;
  commodityLabel?: string;
}

interface DemandDetailsFormProps {
  methods: UseFormReturn<DemandFormValues>;
  fields: { id: string }[];
  commodities: CommodityOption[];
  qualityOptions: QualityOption[];
  unitOptionsFor: (commodityId: number | null) => UnitOption[];
  currencySymbol?: string;
  fieldErrors: unknown;
  showDeposits: boolean;
  depositLines: DepositLine[];
  onAddCommodity: () => void;
  onRemoveCommodity: (index: number) => void;
  onBrowseMarket: (params: BrowseMarketParams) => void;
  t: TranslateFn;
}

export const DemandDetailsForm = ({
  methods,
  fields,
  commodities,
  qualityOptions,
  unitOptionsFor,
  currencySymbol,
  fieldErrors,
  showDeposits,
  depositLines,
  onAddCommodity,
  onRemoveCommodity,
  onBrowseMarket,
  t,
}: DemandDetailsFormProps) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = methods;

  const serverError = (name: string) => getErrors(fieldErrors, name) || "";
  const itemsError = typeof errors.items?.message === "string" ? errors.items.message : "";

  if (showDeposits) {
    return (
      <div className="space-y-6">
        {depositLines.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("marketOfferDetails.form.noMatchingDepositsBody")}
          </p>
        ) : (
          depositLines.map(line => (
            <MatchingDepositsPanel
              key={line.key}
              commodityId={line.commodityId}
              quality={line.quality}
              unitOfMeasureId={line.unitOfMeasureId}
              commodityLabel={line.commodityLabel}
              onBrowseMarket={onBrowseMarket}
              t={t}
            />
          ))
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {fields.map((field, index) => (
          <CommodityLineRow
            key={field.id}
            index={index}
            control={control}
            setValue={setValue}
            commodities={commodities}
            qualityOptions={qualityOptions}
            unitOptionsFor={unitOptionsFor}
            currencySymbol={currencySymbol}
            canRemove={fields.length > 1}
            onRemove={() => onRemoveCommodity(index)}
            serverError={serverError}
            t={t}
          />
        ))}

        {itemsError && (
          <p role="alert" className="text-destructive text-sm">
            {itemsError}
          </p>
        )}

        <div>
          <WFPButton type="button" variant="secondary" onClick={onAddCommodity}>
            <Plus className="size-4" />
            {t("marketOfferDetails.form.addCommodity")}
          </WFPButton>
        </div>
      </div>

      <Controller
        control={control}
        name="negotiable"
        render={({ field }) => (
          <div className="flex items-center gap-2">
            <Checkbox
              id="negotiable"
              checked={field.value}
              onCheckedChange={checked => field.onChange(checked === true)}
            />
            <Label htmlFor="negotiable" className="cursor-pointer font-normal">
              {t("marketOfferDetails.form.negotiable")}
            </Label>
          </div>
        )}
      />
    </div>
  );
};
